import { useCallback, useMemo } from 'react';
import cookie from 'browser-cookies';
import ChatReconnectContext from '@microsoft/omnichannel-chat-sdk/lib/core/ChatReconnectContext';
import { ILogger } from '@bingads-webui-react/msa-copilot-common';

const LIVE_CHAT_RECONNECT_CONTEXT = 'LIVE_CHAT_RECONNECT_CONTEXT';

interface LiveChatReconnectContext {
  chatReconnectContext?: ChatReconnectContext;
  copilotConversationId?: string;
}

export const useLiveAgentCookies = (logger?: ILogger) => {
  const getCookieDomain = useCallback(() => window.location.hostname.replace(/^.*\.?ui/, ''), []);

  const cookieOptions = useMemo(() => ({
    domain: getCookieDomain(),
    samesite: 'Strict' as const,
    secure: true,
  }), []);

  const getLiveChatContext = useCallback(() => {
    try {
      const contextStr = cookie.get(LIVE_CHAT_RECONNECT_CONTEXT);
      if (!contextStr) {
        return {} as LiveChatReconnectContext;
      }
      return JSON.parse(contextStr) as LiveChatReconnectContext;
    } catch (error: any) {
      logger?.error(`Live Agent: Error getting live chat cookies: ${error.message}`);
      return {} as LiveChatReconnectContext;
    }
  }, [logger]);

  const setLiveChatContext = useCallback((context: LiveChatReconnectContext) => {
    try {
      cookie.set(LIVE_CHAT_RECONNECT_CONTEXT, JSON.stringify(context), cookieOptions);
      logger?.trace('Live Agent: Saved live chat context to cookie');
    } catch (error: any) {
      logger?.error(`Live Agent: Error setting live chat context cookie: ${error.message}`);
    }
  }, [logger, cookieOptions]);

  const clearLiveChatCookies = useCallback(() => {
    try {
      const cookieEraseOptions = {
        path: '/',
        domain: getCookieDomain(),
      };

      cookie.erase(LIVE_CHAT_RECONNECT_CONTEXT, cookieEraseOptions);

      logger?.trace('Live Agent: Cleared live chat cookies');
    } catch (error: any) {
      logger?.error(`Live Agent: Error clearing live chat cookies: ${error.message}`);
    }
  }, [logger, getCookieDomain]);

  return {
    getLiveChatContext,
    setLiveChatContext,
    clearLiveChatCookies,
  };
};
